

import React, { useState, useCallback } from 'react';
import { PMIInputs, PMIRemovalRule } from '../types';
import { usePMICalculator } from '../hooks/usePMICalculator';
import { InputCard, InputRow } from './InputCard';
import { InfoTooltip } from './InfoTooltip';
import { PMIResultsDisplay } from './PMIResultsDisplay';
import { PMIDataTable } from './PMIDataTable';

const oneYearAgo = () => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().split('T')[0];
};

export const PMIRemovalCalculator: React.FC = () => {
  const [inputs, setInputs] = useState<PMIInputs>({
    currentBalance: 380000,
    originalHomeValue: 400000,
    interestRate: 6.5,
    originationDate: oneYearAgo(),
    loanTerm: 30,
    monthlyPMI: 150,
    removalRule: PMIRemovalRule.AUTOMATIC_78,
    extraPayment: 500,
    investmentReturnRate: 7,
  });

  const [submittedInputs, setSubmittedInputs] = useState<PMIInputs | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const results = usePMICalculator(submittedInputs);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setInputs(prev => {
        let finalValue: string | number;
        if (name === 'removalRule' || name === 'originationDate') {
            finalValue = value;
        } else {
            finalValue = parseFloat(value) || 0;
        }
        return { ...prev, [name]: finalValue };
    });
    if (submittedInputs) setSubmittedInputs(null);
  };

  const handleCalculate = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmittedInputs(null);
    setTimeout(() => {
        setSubmittedInputs(inputs);
        setTimeout(() => setIsLoading(false), 300);
    }, 50);
  }, [inputs]);

  const inputClass = "w-full p-2 border rounded bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white transition-colors focus:ring-2 focus:ring-bp-light-blue outline-none";

  return (
    <>
        <header className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-bp-slate dark:text-white">Kill PMI Calculator</h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 mt-2">
                Should you aggressively pay down your mortgage to remove Mortgage Insurance?
            </p>
        </header>

        <main>
            <form onSubmit={handleCalculate}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <InputCard title="Loan & PMI Details">
                         <InputRow label="Current Balance" id="currentBalance">
                            <div className="flex items-center">
                                <span className="mr-2 text-gray-500 dark:text-gray-400">$</span>
                                <input type="number" id="currentBalance" name="currentBalance" value={inputs.currentBalance} onChange={handleChange} className={inputClass} required />
                            </div>
                        </InputRow>
                        <InputRow label="Original Home Value" id="originalHomeValue">
                            <div className="flex items-center">
                                <span className="mr-2 text-gray-500 dark:text-gray-400">$</span>
                                <input type="number" id="originalHomeValue" name="originalHomeValue" value={inputs.originalHomeValue} onChange={handleChange} className={inputClass} required />
                                <InfoTooltip text="Banks typically use the ORIGINAL value (or purchase price), not current market value, to determine the 80% LTV threshold for PMI removal." />
                            </div>
                        </InputRow>
                         <InputRow label="Monthly PMI Cost" id="monthlyPMI">
                            <div className="flex items-center">
                                <span className="mr-2 text-gray-500 dark:text-gray-400">$</span>
                                <input type="number" id="monthlyPMI" name="monthlyPMI" value={inputs.monthlyPMI} onChange={handleChange} className={inputClass} required />
                            </div>
                        </InputRow>
                         <InputRow label="Removal Rule" id="removalRule">
                             <select id="removalRule" name="removalRule" value={inputs.removalRule} onChange={handleChange} className={inputClass}>
                                {Object.values(PMIRemovalRule).map(rule => (
                                    <option key={rule} value={rule}>{rule}</option>
                                ))}
                            </select>
                        </InputRow>
                    </InputCard>

                    <InputCard title="Strategy Inputs">
                        <InputRow label="Interest Rate (%)" id="interestRate">
                            <input type="number" step="0.125" id="interestRate" name="interestRate" value={inputs.interestRate} onChange={handleChange} className={inputClass} required />
                        </InputRow>
                         <InputRow label="Origination Date" id="originationDate">
                            <input type="date" id="originationDate" name="originationDate" value={inputs.originationDate} onChange={handleChange} className={inputClass} required />
                        </InputRow>
                        <InputRow label="Loan Term (Yrs)" id="loanTerm">
                            <input type="number" step="1" id="loanTerm" name="loanTerm" value={inputs.loanTerm} onChange={handleChange} className={inputClass} required />
                        </InputRow>
                        <hr className="my-4 dark:border-slate-600"/>
                         <InputRow label="Extra Cash Monthly" id="extraPayment">
                             <div className="flex items-center">
                                <span className="mr-2 text-gray-500 dark:text-gray-400">$</span>
                                <input type="number" id="extraPayment" name="extraPayment" value={inputs.extraPayment} onChange={handleChange} className={inputClass} required />
                                <InfoTooltip text="How much extra money do you have available each month to either invest OR pay down the mortgage?" />
                             </div>
                        </InputRow>
                         <InputRow label="Inv. Return Rate (%)" id="investmentReturnRate">
                            <input type="number" step="0.1" id="investmentReturnRate" name="investmentReturnRate" value={inputs.investmentReturnRate} onChange={handleChange} className={inputClass} required />
                        </InputRow>
                    </InputCard>
                </div>

                <div className="mt-6 text-center">
                    <button type="submit" className="bg-bp-dark-blue hover:bg-bp-slate text-white font-bold py-3 px-8 rounded-lg text-lg transition-colors shadow-lg disabled:bg-gray-400" disabled={isLoading}>
                        {isLoading ? 'Calculating...' : 'Run Analysis'}
                    </button>
                </div>
            </form>

            <PMIResultsDisplay results={results} isLoading={isLoading} />
            {results && !isLoading && <PMIDataTable data={results.monthlyData} />}
        </main>
    </>
  );
};